import { useState } from 'react';
import { Check, Copy, Download } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import type { ExtensionDownload } from './extensionDownload';

const Code = ({ children }: { children: React.ReactNode }) => (
  <code className="rounded bg-black/5 dark:bg-white/10 px-1.5 py-0.5 font-mono text-[12px] studio-text-primary">{children}</code>
);

export function GetExtensionDialog({ download, open, onOpenChange }: { download: ExtensionDownload; open: boolean; onOpenChange: (open: boolean) => void }) {
  const [copied, setCopied] = useState(false);
  // A page can't link to chrome:// addresses; the person pastes it.
  const copy = async () => {
    await navigator.clipboard?.writeText('chrome://extensions').catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Install 4K Labs Capture</DialogTitle>
          <DialogDescription>Version {download.version} · for Chrome</DialogDescription>
        </DialogHeader>
        <a
          href={download.href}
          download
          className="inline-flex w-fit items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition"
        >
          <Download className="h-4 w-4" />
          Download the extension
        </a>
        <ol className="list-decimal space-y-2.5 pl-5 text-sm studio-text-secondary">
          <li>Unzip the file. You get a folder named <Code>{download.folder}</Code>.</li>
          <li>
            In Chrome, open <Code>chrome://extensions</Code>{' '}
            <button type="button" onClick={copy} className="inline-flex items-center gap-1 align-middle text-xs font-medium text-primary hover:underline">
              {copied ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
              {copied ? 'Copied' : 'Copy'}
            </button>{' '}
            and turn on <b className="studio-text-primary">Developer mode</b>, top right.
          </li>
          <li>Click <b className="studio-text-primary">Load unpacked</b> and choose the <Code>{download.folder}</Code> folder.</li>
          <li>
            Open the extension, set <b className="studio-text-primary">Upload to</b> to <b className="studio-text-primary">{download.server}</b>, then click{' '}
            <b className="studio-text-primary">Connect to 4K Labs</b>.
          </li>
        </ol>
        <p className="text-xs studio-text-secondary">
          Have an older version? Remove it on the same page first, then load this one. Loaded this one over it already? Click
          the reload arrow on its card there, or Chrome can keep running the old version.
        </p>
      </DialogContent>
    </Dialog>
  );
}
